import { useState } from 'react';

import {
  BRAND_PROFILE_SECTIONS,
  type ProfileField,
  type ProfileSection,
  type ProfileSource,
} from '../brand-profile-fields';
import {
  Button,
  ErrorNotice,
  Field,
  Panel,
  SelectInput,
  TextArea,
  TextInput,
} from './primitives';
import { SectionHeader } from './primitives';
import { ColorField, ImageUploadField, ListField, RepeatableRows } from './form-fields';
import type { RepeatableRow } from './form-fields';
import { BRAND_FONTS, ensureBrandFont } from '../brand-fonts';

/** The two records the form edits, as loaded from the API. */
export type ProfileValues = Record<string, unknown> | null;

export interface BrandProfileFormProps {
  readonly brand: ProfileValues;
  readonly messaging: ProfileValues;
  /** False renders every control disabled — see the caller's explanation banner. */
  readonly canEdit: boolean;
  /** Saves one section's record. Rejecting shows the message against that section. */
  readonly onSave: (source: ProfileSource, patch: Record<string, unknown>) => Promise<void>;
  /** Uploads an image and resolves to its URL. Omit to offer only URL pasting. */
  readonly upload?: (file: File) => Promise<string>;
  readonly sections?: readonly ProfileSection[];
}

/** A section's fields all live on one record, so a section saves as one patch. */
function sourceOf(section: ProfileSection): ProfileSource {
  return section.fields[0]?.source ?? 'messaging';
}

/**
 * The Brand and Messaging Profile, as one editable form.
 *
 * Shared by both apps on purpose: BCE staff and the client's own admins edit
 * the same profile, and if the two forms drifted they would end up describing
 * different things to the two people most likely to compare notes. Field
 * labels and descriptions come from `BRAND_PROFILE_SECTIONS` for the same
 * reason.
 *
 * Saving is per section rather than per keystroke or per whole record: the
 * sections are long, each maps to one record, and a whole-record save from a
 * stale tab would silently revert someone else's edit.
 *
 * @param props - The two records, whether they may be edited, and the save.
 * @returns The form.
 */
export function BrandProfileForm({
  brand,
  messaging,
  canEdit,
  onSave,
  upload,
  sections = BRAND_PROFILE_SECTIONS,
}: BrandProfileFormProps): React.ReactElement {
  // One draft per record, seeded from what the server holds.
  const [draft, setDraft] = useState<Record<ProfileSource, Record<string, unknown>>>(() => ({
    brand: { ...(brand ?? {}) },
    messaging: { ...(messaging ?? {}) },
  }));
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  /** Reads a field's current draft value. */
  function valueOf(field: ProfileField): unknown {
    return draft[field.source][field.key];
  }

  /** Writes a field into its record's draft. */
  function setValue(field: ProfileField, value: unknown): void {
    setSavedKey(null);
    setDraft((current) => ({
      ...current,
      [field.source]: { ...current[field.source], [field.key]: value },
    }));
  }

  /** Saves one section's fields as a patch on its record. */
  async function saveSection(section: ProfileSection): Promise<void> {
    const source = sourceOf(section);
    const patch: Record<string, unknown> = {};
    for (const field of section.fields) {
      const value = draft[source][field.key];
      // Blank text means "clear this", which the API distinguishes from a
      // field that was simply not part of this section's patch.
      patch[field.key] = typeof value === 'string' && value.trim() === '' ? null : value ?? null;
    }

    setSavingKey(section.key);
    setErrors((current) => ({ ...current, [section.key]: '' }));
    try {
      await onSave(source, patch);
      setSavedKey(section.key);
    } catch (err) {
      setErrors((current) => ({
        ...current,
        [section.key]: err instanceof Error ? err.message : 'Could not save these changes.',
      }));
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div className="grid gap-8">
      {sections.map((section) => (
        <section key={section.key}>
        <SectionHeader title={section.title} description={section.description} />
        <Panel className="p-6">

          {errors[section.key] ? (
            <ErrorNotice inline title="Could not save" message={errors[section.key] ?? ''} />
          ) : null}

          <div className="grid gap-5">
            {section.fields.map((field) => (
              <ProfileControl
                key={field.key}
                field={field}
                value={valueOf(field)}
                onChange={(value) => setValue(field, value)}
                disabled={!canEdit}
                // The dark-logo preview is drawn on the sidebar colour as it
                // currently stands in the form, so the pairing being chosen is
                // the pairing shown.
                sidebarColor={
                  typeof draft.brand['sidebarColor'] === 'string'
                    ? (draft.brand['sidebarColor'] as string)
                    : '#001d52'
                }
                {...(upload === undefined ? {} : { upload })}
              />
            ))}
          </div>

          {canEdit ? (
            <div className="mt-5 flex items-center justify-end gap-3">
              {savedKey === section.key ? (
                <span className="text-portal-micro text-portal-muted">Saved.</span>
              ) : null}
              <Button
                variant="primary"
                onClick={() => void saveSection(section)}
                disabled={savingKey === section.key}
              >
                {savingKey === section.key ? 'Saving…' : `Save ${section.title.toLowerCase()}`}
              </Button>
            </div>
          ) : null}
        </Panel>
        </section>
      ))}
    </div>
  );
}

/** Renders the control a field's `kind` calls for. */
function ProfileControl({
  field,
  value,
  onChange,
  disabled,
  upload,
  sidebarColor,
}: {
  readonly field: ProfileField;
  readonly value: unknown;
  readonly onChange: (value: unknown) => void;
  readonly disabled: boolean;
  readonly upload?: (file: File) => Promise<string>;
  readonly sidebarColor: string;
}): React.ReactElement {
  const text = typeof value === 'string' ? value : '';
  const list = Array.isArray(value) ? (value as string[]) : [];
  const rows = Array.isArray(value) ? (value as RepeatableRow[]) : [];

  // Offered only once a field actually differs from its default: a reset link
  // beside an untouched field is noise, and beside a changed one it is the way
  // back from a colour that made the portal unreadable.
  const resettable =
    !disabled && field.defaultValue !== undefined && text !== field.defaultValue;
  const reset = resettable ? (
    <button
      type="button"
      onClick={() => onChange(field.defaultValue)}
      className="text-portal-micro font-semibold text-brand hover:underline"
    >
      Reset to default
    </button>
  ) : undefined;

  switch (field.kind) {
    case 'list':
      return (
        <ListField
          label={field.label}
          description={field.description}
          {...(reset === undefined ? {} : { action: reset })}
          value={list}
          onChange={onChange}
          disabled={disabled}
          {...(field.placeholder === undefined ? {} : { placeholder: field.placeholder })}
        />
      );

    case 'rows':
      return (
        <RepeatableRows
          label={field.label}
          description={field.description}
          columns={field.columns ?? []}
          value={rows}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'color':
      return (
        <ColorField
          label={field.label}
          description={field.description}
          {...(reset === undefined ? {} : { action: reset })}
          value={text === '' ? '#000000' : text}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'image':
      return (
        <ImageUploadField
          label={field.label}
          description={field.description}
          {...(reset === undefined ? {} : { action: reset })}
          value={text}
          onChange={onChange}
          disabled={disabled}
          {...(field.previewOnSidebar === true ? { previewBackground: sidebarColor } : {})}
          {...(upload === undefined ? {} : { upload })}
        />
      );

    case 'font':
      return (
        <LabelledControl field={field} action={reset}>
          {(id) => (
            <SelectInput
              id={id}
              value={text === '' ? (field.defaultValue ?? '') : text}
              disabled={disabled}
              onChange={(event) => {
                // Fetch it now so the choice is visible while still on the
                // form, rather than only after saving and reloading.
                ensureBrandFont(event.target.value);
                onChange(event.target.value);
              }}
              // Renders each option in its own face where the browser allows
              // it, so the list can be judged by eye.
              style={{ fontFamily: text === '' ? undefined : text }}
            >
              {BRAND_FONTS.map((font) => (
                <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                  {font.label}
                </option>
              ))}
            </SelectInput>
          )}
        </LabelledControl>
      );

    case 'prose':
      return (
        <LabelledControl field={field} action={reset}>
          {(id) => (
            <TextArea
              id={id}
              rows={3}
              value={text}
              disabled={disabled}
              placeholder={field.placeholder}
              onChange={(event) => onChange(event.target.value)}
            />
          )}
        </LabelledControl>
      );

    default:
      return (
        <LabelledControl field={field} action={reset}>
          {(id) => (
            <TextInput
              id={id}
              type={field.kind === 'url' ? 'url' : 'text'}
              value={text}
              disabled={disabled}
              placeholder={field.placeholder}
              onChange={(event) => onChange(event.target.value)}
            />
          )}
        </LabelledControl>
      );
  }
}

/** Field wrapper putting the description above the control. */
function LabelledControl({
  field,
  children,
  action,
}: {
  readonly field: ProfileField;
  readonly children: (id: string) => React.ReactNode;
  readonly action?: React.ReactNode;
}): React.ReactElement {
  const id = `profile-${field.key}`;
  return (
    <Field
      label={field.label}
      htmlFor={id}
      hint={field.description}
      {...(action === undefined ? {} : { action })}
    >
      {children(id)}
    </Field>
  );
}
